// Effect Analytics Utility
// Tracks user behavior and effect type preferences for recommendation engine

import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, query, where, getDocs, orderBy } from 'firebase/firestore';
import { auth } from '../firebase';

// Generate a simple session ID for grouping user actions
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Track user actions related to effect types
export const trackEffectAction = async (action, effectTypes, additionalData = {}) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.log('No authenticated user for analytics tracking');
      return;
    }

    // Get or create session ID
    let sessionId = sessionStorage.getItem('effectAnalyticsSessionId');
    if (!sessionId) {
      sessionId = generateSessionId();
      sessionStorage.setItem('effectAnalyticsSessionId', sessionId);
    }

    const analyticsData = {
      userId: user.uid,
      action, // 'added_pedal', 'added_want', 'viewed_pedal', 'clicked_recommendation'
      effectTypes: Array.isArray(effectTypes) ? effectTypes : [effectTypes],
      sessionId,
      timestamp: serverTimestamp(),
      ...additionalData
    };

    await addDoc(collection(db, 'effectAnalytics'), analyticsData);
    
    console.log('✅ Effect analytics tracked:', { action, effectTypes, sessionId });
  } catch (error) {
    console.error('❌ Error tracking effect analytics:', error);
  }
};

// Track when user adds a pedal to their pedalboard
export const trackPedalAdded = async (pedalData) => {
  await trackEffectAction('added_pedal', pedalData.effectTypes || [], {
    pedalId: pedalData.id,
    make: pedalData.make,
    model: pedalData.model,
    price: pedalData.price
  });
};

// Track when user adds a want to their Wantlist
export const trackWantAdded = async (wantData) => {
  await trackEffectAction('added_want', wantData.effectTypes || [], {
    wantId: wantData.id,
    make: wantData.make,
    model: wantData.model,
    maxPrice: wantData.maxPrice
  });
};

// Track when user views a pedal listing
export const trackPedalViewed = async (pedalData) => {
  await trackEffectAction('viewed_pedal', pedalData.effectTypes || [], {
    pedalId: pedalData.id,
    make: pedalData.make,
    model: pedalData.model,
    price: pedalData.price
  });
};

// Track when user clicks on a recommendation
export const trackRecommendationClicked = async (recommendedPedal, userWantEffectTypes) => {
  await trackEffectAction('clicked_recommendation', recommendedPedal.effectTypes || [], {
    recommendedPedalId: recommendedPedal.id,
    userWantEffectTypes,
    make: recommendedPedal.make,
    model: recommendedPedal.model,
    price: recommendedPedal.price
  });
};

// Get user's effect type preferences based on their activity
export const getUserEffectPreferences = async (userId) => {
  try {
    // Get user's recent activity (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const q = query(
      collection(db, 'effectAnalytics'),
      where('userId', '==', userId),
      where('timestamp', '>=', thirtyDaysAgo),
      orderBy('timestamp', 'desc')
    );

    const snapshot = await getDocs(q);
    const activities = snapshot.docs.map(doc => doc.data());

    // Calculate effect type preferences based on activity
    const effectCounts = {};
    const effectWeights = {
      'added_pedal': 3,      // High weight - user owns this
      'added_want': 2,       // Medium weight - user wants this
      'viewed_pedal': 1,     // Low weight - user showed interest
      'clicked_recommendation': 1.5 // Medium-high weight - user engaged with recommendation
    };

    activities.forEach(activity => {
      const weight = effectWeights[activity.action] || 1;
      activity.effectTypes.forEach(effectType => {
        effectCounts[effectType] = (effectCounts[effectType] || 0) + weight;
      });
    });

    // Convert to preference scores (0-1)
    const totalActivity = Object.values(effectCounts).reduce((sum, count) => sum + count, 0);
    const preferences = {};
    
    Object.keys(effectCounts).forEach(effectType => {
      preferences[effectType] = effectCounts[effectType] / totalActivity;
    });

    return preferences;
  } catch (error) {
    console.error('❌ Error getting user effect preferences:', error);
    return {};
  }
};

// Get co-occurrence data for effect types
export const getEffectCooccurrence = async (effectType) => {
  try {
    // This would typically be calculated by a Cloud Function
    // For now, return a simple structure
    return {
      effectType,
      commonlyPairedWith: {},
      lastUpdated: new Date(),
      sampleSize: 0
    };
  } catch (error) {
    console.error('❌ Error getting effect co-occurrence:', error);
    return null;
  }
};

// Calculate co-occurrence between effect types (simplified version)
export const calculateEffectCooccurrence = async () => {
  try {
    // Get all analytics data
    const snapshot = await getDocs(collection(db, 'effectAnalytics'));
    const activities = snapshot.docs.map(doc => doc.data());

    // Group by user to find co-occurring effect types
    const userEffectSets = {};
    activities.forEach(activity => {
      if (!userEffectSets[activity.userId]) {
        userEffectSets[activity.userId] = new Set();
      }
      activity.effectTypes.forEach(effectType => {
        userEffectSets[activity.userId].add(effectType);
      });
    });

    // Calculate co-occurrence matrix
    const cooccurrence = {};
    const effectTypes = new Set();
    
    Object.values(userEffectSets).forEach(effectSet => {
      const effectArray = Array.from(effectSet);
      effectArray.forEach(effectType => {
        effectTypes.add(effectType);
        if (!cooccurrence[effectType]) {
          cooccurrence[effectType] = {};
        }
        effectArray.forEach(otherEffect => {
          if (effectType !== otherEffect) {
            cooccurrence[effectType][otherEffect] = (cooccurrence[effectType][otherEffect] || 0) + 1;
          }
        });
      });
    });

    // Convert counts to percentages
    const result = {};
    effectTypes.forEach(effectType => {
      const total = Object.values(cooccurrence[effectType]).reduce((sum, count) => sum + count, 0);
      result[effectType] = {};
      Object.keys(cooccurrence[effectType]).forEach(otherEffect => {
        result[effectType][otherEffect] = cooccurrence[effectType][otherEffect] / total;
      });
    });

    return result;
  } catch (error) {
    console.error('❌ Error calculating effect co-occurrence:', error);
    return {};
  }
};
